import { useCallback, useEffect, useState } from "react";
import { fetchTransactionsByOwner } from "../services/api.js";

/**
 * Last N indexed transactions for the connected wallet. Sourced from
 * the backend (which projects them from the live indexer's Event rows
 * via transaction.service.js → buildTransaction). Per the data-flow
 * spec the frontend never reads transaction history from chain directly.
 *
 * Pass a larger `limit` (e.g. 500) when the consumer needs the full
 * history for chart reconstruction — the default of 8 is sized for the
 * Dashboard's Recent Transactions card.
 *
 * pollMs default is 10s. The backend indexer itself polls the chain
 * every 15s, so a fresh action can take ~15-25s to surface on the
 * Recent Transactions card — the 10s frontend poll keeps the gap as
 * short as it can be without faster indexing.
 */
export function useRecentTransactions(owner, { limit = 8, pollMs = 10_000 } = {}) {
    const [items, setItems] = useState([]);
    const [isLoading, setLoading] = useState(Boolean(owner));
    const [error, setError] = useState(null);

    const refresh = useCallback(async () => {
        if (!owner) {
            setItems([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const data = await fetchTransactionsByOwner(owner, { limit });
            setItems(data?.items || []);
            setError(null);
        } catch (err) {
            setError(err?.message || "Failed to fetch transactions");
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, [owner, limit]);

    useEffect(() => {
        refresh();
        if (!pollMs) return undefined;
        const handle = setInterval(refresh, pollMs);
        return () => clearInterval(handle);
    }, [refresh, pollMs]);

    return { items, isLoading, error, refresh };
}
